/**
 * Resort cache policy — master switch + per-resource enable/TTL.
 *
 * Toggle `enabled` (or a resource's `enabled`) to bypass caching without
 * changing call sites. TTLs are in milliseconds.
 */

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;

export const RESORT_CACHE_CONFIG = {
  /** Master switch for the whole resort cache layer */
  enabled: true,

  /** Fallback TTL when a resource does not define its own */
  defaultTtlMs: 15 * MINUTE,

  resources: {
    /** Hotel list / hotel documents */
    hotels: {
      enabled: true,
      ttlMs: 30 * MINUTE,
    },

    /**
     * Room types (`hotel_rooms`). Needed to resolve a hotel's room plans
     * and to build availability; exposed as its own policy for tuning.
     */
    hotelRooms: {
      enabled: true,
      ttlMs: 30 * MINUTE,
    },

    /** Rate plans under `hotel_rooms/{roomId}/plans` */
    roomPlans: {
      enabled: true,
      ttlMs: 30 * MINUTE,
    },

    /**
     * Computed availability snapshots (and the reservations input used
     * while building them). Keep shorter than hotels/plans.
     */
    availability: {
      enabled: true,
      ttlMs: 2 * MINUTE,
    },
  },
};

export const getResourceCachePolicy = (resourceKey) => {
  const resource = RESORT_CACHE_CONFIG.resources[resourceKey] || {};
  const enabled =
    Boolean(RESORT_CACHE_CONFIG.enabled) && resource.enabled !== false;

  const ttlMs =
    resource.ttlMs != null
      ? Number(resource.ttlMs)
      : Number(RESORT_CACHE_CONFIG.defaultTtlMs);

  return {
    enabled,
    ttlMs: Number.isFinite(ttlMs) && ttlMs >= 0 ? ttlMs : 0,
  };
};

/**
 * Runtime overrides (e.g. from a debug panel or feature flag later).
 * Mutates the shared config object intentionally so the live cache
 * picks up changes without a reload.
 */
export const setResortCacheEnabled = (enabled) => {
  RESORT_CACHE_CONFIG.enabled = Boolean(enabled);
};

export const setResourceCacheEnabled = (resourceKey, enabled) => {
  if (!RESORT_CACHE_CONFIG.resources[resourceKey]) return;
  RESORT_CACHE_CONFIG.resources[resourceKey].enabled = Boolean(enabled);
};

export const setResourceCacheTtlMs = (resourceKey, ttlMs) => {
  if (!RESORT_CACHE_CONFIG.resources[resourceKey]) return;
  const n = Number(ttlMs);
  if (!Number.isFinite(n) || n < 0) return;
  RESORT_CACHE_CONFIG.resources[resourceKey].ttlMs = n;
};
